import type {
	DashboardRuntimeValidationIssue,
	DashboardRuntimeValidationResult,
} from "./dashboard-runtime.types";

const MAX_SOURCE_LENGTH = 100_000;

const FORBIDDEN_PATTERNS: Array<{ pattern: RegExp; rule: string; message: string }> = [
	{
		pattern: /\beval\s*\(/m,
		rule: "no-eval",
		message: "Runtime JSX cannot call eval().",
	},
	{
		pattern: /\bFunction\s*\(/m,
		rule: "no-function-constructor",
		message: "Runtime JSX cannot construct functions dynamically.",
	},
	{
		pattern: /\b(?:window|document|globalThis|process|localStorage|sessionStorage)\b/m,
		rule: "no-global-access",
		message: "Runtime JSX cannot access browser or process globals directly.",
	},
	{
		pattern: /\b(?:fetch|XMLHttpRequest|WebSocket|Worker)\b/m,
		rule: "no-network-primitives",
		message: "Runtime JSX cannot access network or worker primitives directly.",
	},
	{
		pattern: /\.__proto__\b/m,
		rule: "no-proto-access",
		message: "Runtime JSX cannot access __proto__.",
	},
	{
		pattern: /\.prototype\s*[=\[]/m,
		rule: "no-prototype-mutation",
		message: "Runtime JSX cannot mutate prototype chains.",
	},
	{
		pattern: /\.constructor\s*[(\[]/m,
		rule: "no-constructor-access",
		message: "Runtime JSX cannot access .constructor().",
	},
];

const pushIssue = (issues: DashboardRuntimeValidationIssue[], rule: string, message: string) => {
	issues.push({ message, rule });
};

export const validateDashboardRuntimeSource = (
	source: string,
): DashboardRuntimeValidationResult => {
	const issues: DashboardRuntimeValidationIssue[] = [];
	const trimmed = source.trim();

	if (!trimmed) {
		pushIssue(issues, "non-empty", "Runtime JSX source is empty.");
	}

	if (trimmed.length > MAX_SOURCE_LENGTH) {
		pushIssue(
			issues,
			"max-length",
			`Runtime JSX exceeds the ${MAX_SOURCE_LENGTH.toLocaleString()} character limit.`,
		);
	}

	if (!/export\s+default\b/m.test(trimmed)) {
		pushIssue(issues, "default-export", "Runtime JSX must default export a React component.");
	}

	for (const entry of FORBIDDEN_PATTERNS) {
		if (entry.pattern.test(trimmed)) {
			pushIssue(issues, entry.rule, entry.message);
		}
	}

	if (issues.length === 0) {
		return { ok: true, issues };
	}

	return { ok: false, issues };
};
